import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useAuth } from '@/lib/auth-context'
import { useTenant } from '@/lib/tenant-context'
import {
  Button,
  Input,
  Label,
} from '@lead-magnifier/ui'

export const Route = createFileRoute('/login')({
  component: LoginPage,
})

function LoginPage() {
  const { signIn } = useAuth()
  const tenant = useTenant()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await signIn(email, password)
      navigate({ to: '/dashboard' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-warm bg-noise flex">
      {/* Side panel — hidden on mobile */}
      <div
        className="hidden lg:flex w-1/3 flex-col justify-end p-12 text-white"
        style={{ backgroundColor: tenant.branding.primary_color }}
      >
        <h2 className="font-display text-3xl font-bold leading-tight">
          Leads erfassen.<br />
          Kontakte pflegen.<br />
          Messen nutzen.
        </h2>
        <p className="mt-4 text-white/60 text-sm leading-relaxed">
          Die Plattform für professionelles Lead-Management auf Messen und Events.
        </p>
      </div>

      {/* Login form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm animate-fade-in-up">
          <div className="text-center mb-10">
            <img
              src={tenant.branding.logo_url}
              alt={tenant.branding.name}
              className="h-10 w-auto mx-auto mb-8 object-contain"
            />
            <h1 className="font-display text-3xl font-bold tracking-tight">
              Willkommen zurück
            </h1>
            <p className="mt-2 text-muted-foreground text-sm">
              Melde dich an, um deine Events zu verwalten
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="rounded-lg bg-destructive/8 border border-destructive/20 px-4 py-3 text-sm text-destructive animate-scale-in">
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                E-Mail
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="name@beispiel.at"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 bg-white border-border/60 focus:border-primary transition-colors"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Passwort
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Vergessen?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 bg-white border-border/60 focus:border-primary transition-colors"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-sm font-semibold tracking-wide btn-premium rounded-lg"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Wird angemeldet...
                </span>
              ) : (
                'Anmelden'
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-8">
            Noch kein Konto?{' '}
            <Link to="/register" className="text-foreground font-medium hover:text-primary transition-colors">
              Registrieren
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
